import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CONSTANTS from "../utils/constants";
import axios from "axios";
import { useTranslation } from "react-i18next";

export default function MobileUpdatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const emptyForm = {
    platform: "android",
    updateType: "",
    targetVersion: "",
    storeUrl: "",
    mandatoryReason: "",
    optionalReason: "",
  };

  const [formData, setFormData] = useState(emptyForm);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /* ================= FETCH ================= */
  const fetchByPlatform = async (platform) => {
    try {
      setIsLoading(true);
      const res = await axios.get(
        `${CONSTANTS.baseRoute}/mobile-updates/latest/${platform}`
      );

      const data = res.data;

      setFormData({
        platform: data.platform,
        updateType: data.update_type === "mandatory" ? "zorunlu" : "not_must",
        currentVersion: data.current_version || "",
        targetVersion: data.target_version || "",
        storeUrl: data.store_url || "",
        mandatoryReason: data.update_type === "mandatory" ? data.reason : "",
        optionalReason: data.update_type === "optional" ? data.reason : "",
      });
    } catch (err) {
      setFormData((prev) => ({
        ...emptyForm,
        platform: prev.platform,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchByPlatform(formData.platform);
  }, []);

  /* ================= HANDLERS ================= */
  const handlePlatformChange = (platform) => {
    setFormData((prev) => ({ ...prev, platform }));
    fetchByPlatform(platform);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      updateType: type,
      mandatoryReason: "",
      optionalReason: "",
    }));
  };

  /* ================= VALIDATION ================= */
  const isFormValid = () => {
    if (!formData.updateType || !formData.targetVersion || !formData.storeUrl)
      return false;

    if (formData.updateType === "zorunlu" && !formData.mandatoryReason)
      return false;

    return true;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      alert(t("validation.fillAll"));
      return;
    }

    const payload = {
      platform: formData.platform,
      updateType: formData.updateType,
      targetVersion: formData.targetVersion,
      storeUrl: formData.storeUrl,
      reason:
        formData.updateType === "zorunlu"
          ? formData.mandatoryReason
          : formData.optionalReason,
    };

    try {
      setIsSaving(true);
      await axios.post(`${CONSTANTS.baseRoute}/mobile-updates`, payload);
      alert(t("mobileUpdate.saved"));
      fetchByPlatform(formData.platform);
    } catch (err) {
      alert(t("serverError"));
    } finally {
      setIsSaving(false);
    }
  };

  /* ================= STYLES ================= */
  const styles = {
    card: {
      borderRadius: "16px",
      border: "none",
      boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
      background: "#ffffff",
      overflow: "hidden",
    },
    platformBtn: (isActive, color) => ({
      flex: 1,
      padding: "12px",
      borderRadius: "12px",
      border: isActive ? `2px solid ${color}` : "2px solid #f0f2f5",
      background: isActive ? `${color}10` : "transparent",
      color: isActive ? color : "#6c757d",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    }),
    typeCard: (isActive, type) => {
      const color = type === "zorunlu" ? "#dc3545" : "#198754";
      return {
        cursor: "pointer",
        borderRadius: "12px",
        border: isActive ? `2px solid ${color}` : "1px solid #e9ecef",
        background: isActive ? `${color}08` : "#fff",
        padding: "15px",
        textAlign: "center",
      };
    },
    input: {
      borderRadius: "10px",
      padding: "12px 15px",
      backgroundColor: "#f8f9fa",
    },
    label: {
      fontSize: "0.85rem",
      fontWeight: "600",
      marginBottom: "6px",
      display: "block",
    },
  };

  /* ================= UI ================= */
  return (
    <div className="admin-main pt-2 pb-3 px-3">
      <div className="d-flex align-items-center gap-3 mb-4">
        <button className="btn admin-icon-btn" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left"></i>
        </button>
        <button className="btn admin-icon-btn" onClick={() => navigate(+1)}>
          <i className="bi bi-chevron-right"></i>
        </button>
        <div>
          <h5 className="fw-bold mb-0">{t("mobileUpdate.title")}</h5>
          <small className="text-muted">
            {t("mobileUpdate.subtitle")}
          </small>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card shadow position-relative" style={styles.card}>
            {isLoading && (
              <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75">
                <div className="spinner-border text-primary"></div>
              </div>
            )}

            <div className="card-body p-4">
              {/* PLATFORM */}
              <div className="d-flex gap-3 mb-4">
                <button
                  type="button"
                  style={styles.platformBtn(
                    formData.platform === "android",
                    "#198754"
                  )}
                  onClick={() => handlePlatformChange("android")}
                >
                  <i className="bi bi-android2"></i> Android
                </button>
                <button
                  type="button"
                  style={styles.platformBtn(
                    formData.platform === "ios",
                    "#0d6efd"
                  )}
                  onClick={() => handlePlatformChange("ios")}
                >
                  <i className="bi bi-apple"></i> iOS
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {/* VERSIONS */}
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label style={styles.label}>
                      {t("mobileUpdate.currentVersion")}
                    </label>
                    <input
                      className="form-control"
                      value={formData.targetVersion}
                      disabled
                      style={styles.input}
                    />
                  </div>
                  <div className="col-6">
                    <label style={styles.label}>
                      {t("mobileUpdate.newVersion")} *
                    </label>
                    <input
                      className="form-control"
                      name="targetVersion"
                      value={formData.targetVersion}
                      onChange={handleInputChange}
                      style={styles.input}
                      required
                    />
                  </div>
                </div>

                {/* STORE URL */}
                <div className="mb-4">
                  <label style={styles.label}>
                    {t("mobileUpdate.storeUrl")} *
                  </label>
                  <input
                    className="form-control"
                    name="storeUrl"
                    value={formData.storeUrl}
                    onChange={handleInputChange}
                    style={styles.input}
                  />
                </div>

                {/* UPDATE TYPE */}
                <div className="row g-3 mb-4">
                  <div className="col-6">
                    <div
                      onClick={() => handleUpdateTypeChange("zorunlu")}
                      style={styles.typeCard(
                        formData.updateType === "zorunlu",
                        "zorunlu"
                      )}
                    >
                      <strong>{t("mobileUpdate.mandatory")}</strong>
                    </div>
                  </div>
                  <div className="col-6">
                    <div
                      onClick={() => handleUpdateTypeChange("not_must")}
                      style={styles.typeCard(
                        formData.updateType === "not_must",
                        "not_must"
                      )}
                    >
                      <strong>{t("mobileUpdate.optional")}</strong>
                    </div>
                  </div>
                </div>

                {/* REASON */}
                {formData.updateType === "zorunlu" && (
                  <textarea
                    className="form-control mb-3"
                    name="mandatoryReason"
                    value={formData.mandatoryReason}
                    onChange={handleInputChange}
                    placeholder={t("mobileUpdate.mandatoryReason")}
                  />
                )}

                {formData.updateType === "not_must" && (
                  <textarea
                    className="form-control mb-3"
                    name="optionalReason"
                    value={formData.optionalReason}
                    onChange={handleInputChange}
                    placeholder={t("mobileUpdate.optionalReason")}
                  />
                )}

                <button
                  type="submit"
                  className="btn btn-primary w-100 py-3"
                  disabled={!isFormValid() || isSaving}
                >
                  {isSaving ? t("saving") : t("save")}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
